import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import useSWR from 'swr';
import { isAxiosError } from 'axios';
import { toast } from 'App';
import { cardFeesService } from 'views/services/cardFeesService';
import { ICardFees } from 'views/types/cardFees';

interface IFormValues {
  issuanceFeeUsd: number;
  // Shown/edited as a percentage (4 = 4%); converted to a fraction on save.
  topupFeePercent: number;
  authorizationFeeUsd: number;
}

export const CardFees = () => {
  const [saving, setSaving] = useState(false);
  const { data, isLoading, mutate } = useSWR('/api/card-fees', () =>
    cardFeesService.getCardFees()
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IFormValues>({ mode: 'onChange' });

  useEffect(() => {
    if (data) {
      reset({
        issuanceFeeUsd: data.issuanceFeeUsd,
        topupFeePercent: Number((data.topupFeePct * 100).toFixed(4)),
        authorizationFeeUsd: data.authorizationFeeUsd,
      });
    }
  }, [data, reset]);

  const onSubmit = async (values: IFormValues) => {
    try {
      setSaving(true);
      const patch: Partial<ICardFees> = {
        issuanceFeeUsd: Number(values.issuanceFeeUsd),
        topupFeePct: Number(values.topupFeePercent) / 100,
        authorizationFeeUsd: Number(values.authorizationFeeUsd),
      };
      const updated = await cardFeesService.updateCardFees(patch);
      await mutate(updated, { revalidate: false });
      toast({
        title: 'Card fees updated',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      const message = isAxiosError(error)
        ? (error.response?.data?.message ?? error.message)
        : 'Failed to update card fees';
      toast({
        title: 'Update failed',
        description: String(message),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    'w-full rounded-lg border border-gray-300 p-2.5 text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white';
  const labelCls =
    'mb-1 block text-sm font-medium text-navy-700 dark:text-white';
  const hintCls = 'mt-1 text-xs text-gray-400';

  return (
    <div className="mt-5">
      <div className="max-w-xl rounded-2xl bg-white p-6 shadow-sm dark:bg-navy-800">
        <h2 className="mb-1 text-xl font-bold text-navy-700 dark:text-white">
          Card Fees
        </h2>
        <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
          Runtime-editable fees for virtual cards. Changes apply live to the bot
          flows and the transaction webhook &mdash; no redeploy needed.
        </p>

        {isLoading ? (
          <span className="text-gray-500">Loading…</span>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div>
              <label className={labelCls}>Issuance fee (USD)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className={inputCls}
                {...register('issuanceFeeUsd', {
                  required: 'Required',
                  min: { value: 0, message: 'Must be ≥ 0' },
                  valueAsNumber: true,
                })}
              />
              {errors.issuanceFeeUsd && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.issuanceFeeUsd.message}
                </p>
              )}
              <p className={hintCls}>Charged once per card issued.</p>
            </div>

            <div>
              <label className={labelCls}>Top-up fee (%)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                className={inputCls}
                {...register('topupFeePercent', {
                  required: 'Required',
                  min: { value: 0, message: 'Must be ≥ 0' },
                  max: { value: 100, message: 'Must be ≤ 100' },
                  valueAsNumber: true,
                })}
              />
              {errors.topupFeePercent && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.topupFeePercent.message}
                </p>
              )}
              <p className={hintCls}>
                Percentage taken from each top-up (e.g. 4 = 4%).
              </p>
            </div>

            <div>
              <label className={labelCls}>Authorization fee (USD)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className={inputCls}
                {...register('authorizationFeeUsd', {
                  required: 'Required',
                  min: { value: 0, message: 'Must be ≥ 0' },
                  valueAsNumber: true,
                })}
              />
              {errors.authorizationFeeUsd && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.authorizationFeeUsd.message}
                </p>
              )}
              <p className={hintCls}>
                Swept from the card on every transaction (approved and
                declined).
              </p>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-brand-500 px-5 py-2.5 font-medium text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default CardFees;
